// public/scripts/stack-container.js - Reusable component for displaying card stacks (Piles/Harvests)

import { createStackElement } from './krida-ui-card-stack.js';

// Module-level state for managing the single global click listener
let moduleLevelListenerAttached = false;
let currentGlobalClickListener = null; // Stores the actual listener function currently attached

/**
 * Creates a container element for displaying a stack of cards with associated info.
 * Relies on CSS classes defined in stack-container.css for layout and appearance.
 *
 * @param {Array} cards - Array of card objects for the stack.
 * @param {Object} config - Configuration options:
 *  - type: 'pile' | 'harvest' (Required)
 *  - helpers: Object (Required) - KridaPatraHelpers instance.
 *  - pileDetailsModal: Object (Optional, for type='pile') - Modal instance for showing pile details.
 *  - errorModal: Function (Optional, for type='pile') - Function to show error messages.
 *  --- Pile Specific ---
 *  - pileIndex: number (Optional) - Index of the pile on the board.
 *  - pileValue: number | string (Optional) - The calculated value of the pile.
 *  - pilePoints: number | string (Optional) - The total points in the pile (visible + hidden).
 *  - cardCount: number | string (Optional) - The total number of cards in the pile.
 *  - isSelected: boolean (Optional) - Whether the pile is currently selected.
 *  --- Harvest Specific ---
 *  - harvestPoints: number | string (Optional) - Total points for this harvest action.
 *  - isBoardClear: boolean (Optional) - Whether this harvest cleared the board.
 * @returns {{element: HTMLElement} | null} - An object containing the container element, or null on error.
 */
export function createStackContainer(cards = [], config = {}) {
    // --- Input Validation ---
    if (!config.type || (config.type !== 'pile' && config.type !== 'harvest')) {
        console.error("createStackContainer: Invalid or missing 'type' in config ('pile' or 'harvest').", config);
        return null;
    }
    if (!config.helpers) {
        console.error("createStackContainer: Missing 'helpers' in config.", config);
        return null;
    }
    if (config.type === 'pile' && !config.pileDetailsModal) {
        console.warn("createStackContainer: Missing 'pileDetailsModal' for pile type. Details won't be clickable.");
    }
     if (!createStackElement) {
         console.error("createStackContainer: createStackElement import failed?");
         return null;
     }

    const {
        type,
        helpers,
        pileDetailsModal,
        errorModal,
        pileIndex,
        pileValue = 0,
        pilePoints = 0,
        cardCount = 0,
        isSelected = false,
        harvestPoints = 0,
        isBoardClear = false
    } = config;

    // --- Create Main Container ---
    const mainContainer = document.createElement('div');
    mainContainer.className = `stack-container ${type}`; // Base class + type class
    if (type === 'pile' && isSelected) {
        mainContainer.classList.add('selected');
    }
    if (type === 'pile' && pileIndex !== undefined) {
        mainContainer.dataset.pileIndex = pileIndex;
    }
    if (type === 'harvest') {
        mainContainer.classList.add(isBoardClear ? 'status-clear' : 'status-normal');
    }

    // --- Create Card Content Area ---
    const cardContentContainer = document.createElement('div');
    cardContentContainer.className = 'stack-card-content';

    // --- Create Card Stack ---
    const stackResult = createStackElement(cards, { helpers }); // Pass helpers to createStackElement
    if (stackResult && stackResult.element) {
        cardContentContainer.appendChild(stackResult.element);
        
        // To accurately calculate width including padding, element needs to be in the DOM.
        // We append it, measure, then it will be placed correctly by the caller.
        document.body.appendChild(mainContainer);
        const containerStyles = getComputedStyle(mainContainer);
        const paddingX = parseFloat(containerStyles.paddingLeft) + parseFloat(containerStyles.paddingRight);
        document.body.removeChild(mainContainer);

        // Set the width of the main container to match the calculated width of the stack + padding
        if (stackResult.width > 0) {
            mainContainer.style.width = `${stackResult.width + paddingX}px`;
        }
    } else {
        // Handle empty stack case? Maybe show "(empty)" text?
        cardContentContainer.textContent = '(empty)';
        // Apply min-height via CSS if needed for empty ones
    }
    mainContainer.appendChild(cardContentContainer);

    // --- Create Info Elements (Always create, CSS handles visibility) ---

    // Label (Pile specific)
    const labelElement = document.createElement('div');
    labelElement.className = 'stack-label';
    if (type === 'pile') {
        labelElement.textContent = `${pileValue} Pile`;
    }
    mainContainer.appendChild(labelElement);

    // Badges
    const valueBadge = document.createElement('div');
    valueBadge.className = 'stack-info-badge stack-value-badge';
    if (type === 'pile') valueBadge.textContent = pileValue;
    mainContainer.appendChild(valueBadge);

    const pointsBadge = document.createElement('div');
    pointsBadge.className = 'stack-info-badge stack-points-badge';
    if (type === 'pile') pointsBadge.textContent = pilePoints;
    mainContainer.appendChild(pointsBadge);

    const countBadge = document.createElement('div');
    countBadge.className = 'stack-info-badge stack-count-badge';
    if (type === 'pile') countBadge.textContent = cardCount;
    mainContainer.appendChild(countBadge);

    let harvestPointsBadge = null;
    let harvestCountBadge = null;

    // Harvest-specific badges
    if (type === 'harvest') {
        console.log(`[StackContainer] DETAILED LOG: Entering harvest badge creation. cards.length: ${cards.length}, harvestPoints: ${harvestPoints}`);

        // Points Badge (Top-Right, Green/Gold)
        harvestPointsBadge = document.createElement('div'); 
        harvestPointsBadge.className = 'stack-info-badge stack-harvest-points-badge';
        harvestPointsBadge.textContent = harvestPoints;
        mainContainer.appendChild(harvestPointsBadge);
        console.log(`[StackContainer] DETAILED LOG: harvestPointsBadge created and appended. className: ${harvestPointsBadge.className}, textContent: ${harvestPointsBadge.textContent}`);

        // Count Badge (Bottom-Right, Grey)
        harvestCountBadge = document.createElement('div'); 
        harvestCountBadge.className = 'stack-info-badge stack-harvest-count-badge';
        harvestCountBadge.textContent = cards.length;
        mainContainer.appendChild(harvestCountBadge);
        console.log(`[StackContainer] DETAILED LOG: harvestCountBadge created and appended. className: ${harvestCountBadge.className}, textContent: ${harvestCountBadge.textContent}`);
        
        // Log the main container's children to see if they are there
        console.log("[StackContainer] DETAILED LOG: Main container children:", mainContainer.children);

    } else {
        // For non-harvest types, ensure badges are null if event listeners might check them
        harvestPointsBadge = null;
        harvestCountBadge = null;
        // Fallback for old harvestPointsBadge if it was intended for something else or to avoid null parent
        const nonHarvestPointsBadge = document.createElement('div');
        nonHarvestPointsBadge.className = 'stack-info-badge stack-harvest-points-badge';
        nonHarvestPointsBadge.style.display = 'none';
        mainContainer.appendChild(nonHarvestPointsBadge);
    }

    // Text Info (Pile specific)
    const pointsText = document.createElement('div');
    pointsText.className = 'stack-info-text stack-points-text';
    if (type === 'pile') {
        pointsText.textContent = `Points: ${pilePoints}`;
        // Add click listener for pile details modal
        pointsText.addEventListener('click', (event) => {
            console.log(`StackContainer: Points text clicked for pile index: ${pileIndex}`);
            event.stopPropagation();
            if (pileDetailsModal) {
                // Need the full pileData object here ideally.
                // This component only gets cards[], maybe the caller needs to pass pileData?
                // For now, we can't show details easily from here without pileData.
                // Option 1: Pass pileData in config.
                // Option 2: Fire a custom event with pileIndex, let parent handle modal.
                // Let's go with Option 1 for now, assuming config will contain pileData.
                if (config.pileData) {
                    pileDetailsModal.showPileDetailsModal(config.pileData);
                } else {
                     console.error("StackContainer: pileData missing in config, cannot show details.");
                }
            } else {
                console.error("StackContainer: KridaUIPileDetailsModal not available.");
            }
        });
    }
    mainContainer.appendChild(pointsText);

    const countText = document.createElement('div');
    countText.className = 'stack-info-text stack-count-text';
    if (type === 'pile') {
        countText.textContent = `Cards: ${cardCount}`;
    }
    mainContainer.appendChild(countText);

    // --- Create Tooltip Elements (Initially hidden) ---
    const valueTooltip = document.createElement('span');
    valueTooltip.className = 'value-tooltip';
    mainContainer.appendChild(valueTooltip);

    const pointsTooltip = document.createElement('span');
    pointsTooltip.className = 'points-tooltip';
    mainContainer.appendChild(pointsTooltip); 

    const countTooltip = document.createElement('span');
    countTooltip.className = 'count-tooltip';
    mainContainer.appendChild(countTooltip);

    const harvestTooltip = document.createElement('span');
    harvestTooltip.className = 'harvest-tooltip';
    mainContainer.appendChild(harvestTooltip);

    // --- Helper function to generate HTML for a list of cards and their points ---
    function _generateCardPointListHTML(cardArray, helpersRef, getCardPointValueFn) {
        let html = '';
        let sumOfPoints = 0;
        if (cardArray && cardArray.length > 0 && helpersRef && typeof getCardPointValueFn === 'function' && helpersRef.SUIT_SYMBOLS) {
            cardArray.forEach(card => {
                const cardPoint = getCardPointValueFn(card) || 0;
                sumOfPoints += cardPoint;
                html += `${card.value}${helpersRef.SUIT_SYMBOLS[card.suit] || ''}: ${cardPoint} pts<br>`;
            });
        } else if (cardArray && cardArray.length === 0) {
            html += `No cards in this list.<br>`;
        }
        return { html, sumOfPoints };
    }

    // --- Tooltip Management (Instance-specific and Global) ---
    let clickOpenedTooltip = null; // Tracks this instance's click-opened tooltip

    const openTooltip = (tooltipElement, contentCallback) => {
        contentCallback();
        tooltipElement.style.display = 'block';
    };
    const closeTooltip = (tooltipElement) => {
        tooltipElement.style.display = 'none';
    };

    // This function is specific to THIS INSTANCE of stackContainer
    const specificGlobalClickListener = (event) => {
        if (!clickOpenedTooltip) { // This instance doesn't have a click-opened tooltip
            // If this instance was the one that set the listener, it should clean up.
            if (moduleLevelListenerAttached && currentGlobalClickListener === specificGlobalClickListener) {
                document.removeEventListener('click', specificGlobalClickListener, true);
                moduleLevelListenerAttached = false;
                currentGlobalClickListener = null;
            }
            return;
        }

        // Check if the click was inside this instance's main container
        if (mainContainer.contains(event.target)) {
            // Click is inside this stack container. Handled by badge click listeners or ignored if on tooltip.
            return; 
        }

        // If we reach here, the click was outside this instance's main container.
        // So, close this instance's click-opened tooltip.
        closeTooltip(clickOpenedTooltip);
        clickOpenedTooltip = null;
        // And remove this specific global listener if it was the active one.
        if (moduleLevelListenerAttached && currentGlobalClickListener === specificGlobalClickListener) {
            document.removeEventListener('click', specificGlobalClickListener, true);
            moduleLevelListenerAttached = false;
            currentGlobalClickListener = null;
        }
    };

    const addGlobalListener = () => {
        if (moduleLevelListenerAttached && currentGlobalClickListener !== specificGlobalClickListener) {
            // Another instance's listener is active, remove it.
            document.removeEventListener('click', currentGlobalClickListener, true);
            // moduleLevelListenerAttached and currentGlobalClickListener will be updated below.
        }
        // Add this instance's listener if it's not already the active one.
        if (!moduleLevelListenerAttached || currentGlobalClickListener !== specificGlobalClickListener) {
            document.addEventListener('click', specificGlobalClickListener, true); // Capture phase
            moduleLevelListenerAttached = true;
            currentGlobalClickListener = specificGlobalClickListener;
        }
    };

    const removeGlobalListener = () => {
        // Only remove if this instance's listener is currently the active one.
        if (moduleLevelListenerAttached && currentGlobalClickListener === specificGlobalClickListener) {
            document.removeEventListener('click', specificGlobalClickListener, true);
            moduleLevelListenerAttached = false;
            currentGlobalClickListener = null;
        }
    };

    const handleClick = (tooltipElement, contentCallback, event) => {
        event.stopPropagation(); // Important: stop click from immediately triggering global listener
        const isAlreadyClickOpened = clickOpenedTooltip === tooltipElement;

        if (clickOpenedTooltip && !isAlreadyClickOpened) {
            // A different tooltip in this instance was click-opened, close it.
            // No need to call removeGlobalListener here, as addGlobalListener for the new one will handle it.
            closeTooltip(clickOpenedTooltip);
        }

        if (isAlreadyClickOpened) {
            closeTooltip(tooltipElement);
            clickOpenedTooltip = null;
            removeGlobalListener(); // This instance no longer has a click-opened tooltip
        } else {
            openTooltip(tooltipElement, contentCallback);
            clickOpenedTooltip = tooltipElement;
            addGlobalListener(); // This instance now manages the global listener
        }
    };

    const handleMouseOver = (tooltipElement, contentCallback) => {
        if (tooltipElement.style.display === 'block') return;
        if (!clickOpenedTooltip || clickOpenedTooltip === tooltipElement) {
            openTooltip(tooltipElement, contentCallback);
        }
    };
    const handleMouseOut = (tooltipElement) => {
        if (clickOpenedTooltip !== tooltipElement) {
            closeTooltip(tooltipElement);
        }
    };

    let allCardsInPile = null; // Calculate once
    const getAllCardsInPile = () => {
        if (allCardsInPile === null && config.pileData) {
            allCardsInPile = [];
            if (config.pileData.visible && Array.isArray(config.pileData.visible)) { allCardsInPile.push(...config.pileData.visible); }
            if (config.pileData.hidden && Array.isArray(config.pileData.hidden)) { allCardsInPile.push(...config.pileData.hidden); }
        }
        return allCardsInPile || [];
    };

    // --- Attach Event Listeners ---
    if (type === 'pile') {
        const valueContent = () => { valueTooltip.textContent = `${pileValue} Pile`; };
        valueBadge.addEventListener('click', (e) => handleClick(valueTooltip, valueContent, e));
        valueBadge.addEventListener('mouseover', () => handleMouseOver(valueTooltip, valueContent));
        valueBadge.addEventListener('mouseout', () => handleMouseOut(valueTooltip));

        const pointsContent = () => {
            if (config.pileData && helpers && typeof helpers.getCardPointValue === 'function') {
                const currentAllCards = getAllCardsInPile();
                const { html: cardListHTML } = _generateCardPointListHTML(currentAllCards.filter(c => helpers.getCardPointValue(c) > 0), helpers, helpers.getCardPointValue);
                if (cardListHTML && cardListHTML.trim() !== 'No cards in this list.<br>' && cardListHTML.trim() !== '') {
                    pointsTooltip.innerHTML = `Score Value: ${pilePoints}<br>${cardListHTML}`;
                } else {
                    pointsTooltip.innerHTML = `Score Value: ${pilePoints}<br>No scoring cards`;
                }
            } else { pointsTooltip.innerHTML = `Score Value: N/A<br>Point details N/A`; }
        };
        pointsBadge.addEventListener('click', (e) => handleClick(pointsTooltip, pointsContent, e));
        pointsBadge.addEventListener('mouseover', () => handleMouseOver(pointsTooltip, pointsContent));
        pointsBadge.addEventListener('mouseout', () => handleMouseOut(pointsTooltip));

        const countContent = () => {
            if (config.pileData && helpers && typeof helpers.SUIT_SYMBOLS === 'object') {
                const currentAllCards = getAllCardsInPile();
                if (currentAllCards.length > 0) {
                    // For count, we just list cards, not necessarily their points via the new helper.
                    // The new helper is more for point breakdowns. We can adapt or make another helper if needed.
                    // For now, keeping the original card listing for count tooltip to avoid changing its specific format.
                    countTooltip.innerHTML = `Total Cards: ${cardCount}<br>${currentAllCards.map(c => `${c.value}${helpers.SUIT_SYMBOLS[c.suit] || ''}`).join(', ')}`;
                } else { countTooltip.innerHTML = `Total Cards: 0<br>Empty pile`; }
            } else { countTooltip.innerHTML = `Total Cards: N/A<br>Card list N/A`; }
        };
        countBadge.addEventListener('click', (e) => handleClick(countTooltip, countContent, e));
        countBadge.addEventListener('mouseover', () => handleMouseOver(countTooltip, countContent));
        countBadge.addEventListener('mouseout', () => handleMouseOut(countTooltip));
    }

    if (type === 'harvest') {
        const harvestContent = () => {
            let tooltipHTML = `<strong>Harvest Details:</strong><br>`;
            const { html: cardListHTML, sumOfPoints: sumOfCardPointsFromHelper } = _generateCardPointListHTML(cards, helpers, helpers.getCardPointValue);
            
            tooltipHTML += cardListHTML;

            if (config.isBoardClear) {
                const inferredBoardClearBonus = config.harvestPoints - sumOfCardPointsFromHelper;
                tooltipHTML += `Board Clear Bonus: ${inferredBoardClearBonus} pts<br>`;
            }
            
            tooltipHTML += `<strong>Total Harvest Points: ${config.harvestPoints} pts</strong>`;
            harvestTooltip.innerHTML = tooltipHTML;
        };

        if (harvestPointsBadge) { 
            harvestPointsBadge.addEventListener('click', (e) => handleClick(harvestTooltip, harvestContent, e));
            harvestPointsBadge.addEventListener('mouseover', () => handleMouseOver(harvestTooltip, harvestContent));
            harvestPointsBadge.addEventListener('mouseout', () => handleMouseOut(harvestTooltip));
        }

        // Re-enable Tooltip and listeners for harvest count badge
        const harvestCountTooltip = document.createElement('span'); 
        harvestCountTooltip.className = 'harvest-count-tooltip'; 
        mainContainer.appendChild(harvestCountTooltip); 

        const harvestCountContent = () => { harvestCountTooltip.textContent = `Cards Harvested: ${cards.length}`; };
        if (harvestCountBadge) { 
            harvestCountBadge.addEventListener('click', (e) => handleClick(harvestCountTooltip, harvestCountContent, e));
            harvestCountBadge.addEventListener('mouseover', () => handleMouseOver(harvestCountTooltip, harvestCountContent));
            harvestCountBadge.addEventListener('mouseout', () => handleMouseOut(harvestCountTooltip));
        }
    }

    // --- Return Element ---
    // We don't calculate/return width, let CSS handle it.
    return { element: mainContainer };
} 